import { cva, type VariantProps } from 'class-variance-authority'
import { useState } from 'react'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Avatar}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports four sizes: sm (h-8 w-8), md (h-10 w-10, default), lg (h-12 w-12), xl (h-16 w-16)
 * - Circular shape
 * - Fallback shows initials with primary background
 *
 * @example
 * ```tsx
 * const classes = avatarVariants({ size: 'md' })
 * // Returns: "relative inline-flex items-center justify-center ... h-10 w-10 ..."
 * ```
 */
export const avatarVariants = cva(
	'relative inline-flex items-center justify-center overflow-hidden rounded-ds-full bg-ds-primary text-ds-primary-foreground font-normal select-none',
	{
		variants: {
			size: {
				sm: 'h-8 w-8 text-ds-sm',
				md: 'h-10 w-10 text-ds-base',
				lg: 'h-12 w-12 text-ds-lg',
				xl: 'h-16 w-16 text-ds-lg',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Avatar} component.
 *
 * @property size - Avatar size: 'sm' (8) | 'md' (10, default) | 'lg' (12) | 'xl' (16)
 * @property className - Optional additional Tailwind classes
 * @property src - Image URL
 * @property alt - Alt text for image
 * @property fallback - Fallback text (initials) shown if image fails to load
 *
 * @example
 * ```tsx
 * <Avatar
 *   src="/avatar.jpg"
 *   alt="John Doe"
 *   fallback="JD"
 *   size="md"
 * />
 * ```
 */
export interface AvatarProps
	extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
		VariantProps<typeof avatarVariants> {
	src?: string
	alt?: string
	fallback?: string
}

/**
 * Avatar component for displaying user profile images.
 *
 * @remarks
 * - **Styling:** All colors and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small (h-8 w-8), medium (h-10 w-10, default), large (h-12 w-12), xl (h-16 w-16).
 * - **Shape:** Circular (rounded-ds-full).
 * - **Fallback:** Shows initials with the brand primary background if image fails.
 * - **Usage:** User profiles, comment authors, team members.
 *
 * @example Basic usage
 * ```tsx
 * import { Avatar } from '@atlas/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <div>
 *       <Avatar
 *         src="/user-avatar.jpg"
 *         alt="John Doe"
 *         fallback="JD"
 *       />
 *     </div>
 *   )
 * }
 * ```
 *
 * @example With fallback only
 * ```tsx
 * <Avatar fallback="AB" size="lg" />
 * ```
 *
 * @see {@link avatarVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/avatar/avatar.stories.tsx for visual examples
 */
export function Avatar({ size, className, src, alt, fallback, ...props }: AvatarProps) {
	const [imageError, setImageError] = useState(false)

	return (
		<div {...props} className={cn(avatarVariants({ size }), className)}>
			{src && !imageError ? (
				<img src={src} alt={alt} className="h-full w-full object-cover" onError={() => setImageError(true)} />
			) : (
				<span>{fallback}</span>
			)}
		</div>
	)
}
